import { setLayoutMode } from "../LayoutSlice_Store";
import { selectCurrentProjectType } from "../ProjectSlice_Store";
import {
  setChatHistoryError,
  setChatHistoryLoading,
  setMessages,
  setSelectedUseCase,
  setTurnTodoPlan,
  setUseCaseSelected,
} from "./chatSlice";
import { createConversationLoadAbortController } from "./chatModuleState";
import { buildRehydratedHistory } from "./rehydrateMessages";
import { restoreHistoricalOutputs, restoreWorkspacePayload } from "./rehydrateWorkspace";
import type { ChatDispatch } from "./chatSliceTypes";

const TEMP_PROJECT_PREFIX = "temp-";

const isAbortError = (error: unknown): boolean =>
  error instanceof Error && error.name === "AbortError";

/** Rebuild the transcript, the use case and every restored todo plan. */
function applyRehydratedHistory(history: any, dispatch: ChatDispatch, getState: () => any): void {
  const { messages, todoPlans } = buildRehydratedHistory(history.turns);
  console.log("🔄 [REHYDRATE] Rehydrated messages:", messages.length);
  console.log("🔄 [REHYDRATE] Rehydrated todo plans:", Object.keys(todoPlans).length);

  // setMessages replaces the array outright, so the welcome message that
  // resetChatForNewProject would add never reappears.
  dispatch(setMessages(messages));

  // Having a conversation history means the use case was already chosen.
  dispatch(setUseCaseSelected(true));

  // Restore the use case from the project type so UI controls (e.g. file upload
  // being disabled for deal search) stay correct after rehydration.
  const projectType = selectCurrentProjectType(getState());
  if (projectType) {
    dispatch(setSelectedUseCase(projectType));
  }

  for (const [turnId, planData] of Object.entries(todoPlans)) {
    dispatch(
      setTurnTodoPlan({ turnId, plan: planData.plan, scopeSummary: planData.scopeSummary })
    );
  }

  restoreHistoricalOutputs(history.outputsByTurn, dispatch);
  restoreWorkspacePayload(history.latestWorkspacePayload, dispatch);
}

/** Nothing persisted for this project: keep the welcome message, show chat. */
function finishWithNoHistory(dispatch: ChatDispatch): void {
  dispatch(setLayoutMode("chat"));
  dispatch(setChatHistoryLoading(false));
}

/** Fetch the persisted turns. projectService is imported dynamically to avoid a cycle. */
async function fetchHistory(projectId: string, signal: AbortSignal): Promise<any> {
  const { getConversationHistory } = await import("../services/projectService");
  console.log("🔄 [REHYDRATE] Fetching conversation history from API");
  return getConversationHistory(projectId, signal);
}

/** Cancellations and 404s are routine; anything else is surfaced to the user. */
function reportRehydrationFailure(error: unknown, dispatch: ChatDispatch): void {
  // Aborts are expected when the user switches projects/chats
  if (isAbortError(error)) {
    console.log(
      "🛑 [REHYDRATE] Rehydration was cancelled (AbortError) - this is expected during project switch"
    );
    dispatch(setChatHistoryLoading(false));
    return;
  }

  const errorMessage =
    error instanceof Error ? error.message : "Failed to load conversation history";

  if (errorMessage.includes("404") || errorMessage.includes("not found")) {
    console.log("🔄 [REHYDRATE] Conversation not found (404) - showing fresh chat view");
    dispatch(setChatHistoryLoading(false));
    dispatch(setLayoutMode("chat"));
    return;
  }

  console.error("❌ [REHYDRATE] Failed to rehydrate conversation:", error);
  dispatch(setChatHistoryError(errorMessage));
  dispatch(setChatHistoryLoading(false));
  dispatch(setLayoutMode("chat"));
}

/**
 * Rehydrate conversation history for a project: fetch the historical turns from
 * the backend and rebuild the chat state.
 *
 * Expected API response schema (from conversation_history.py):
 * {
 *   conversationId: string,
 *   turns: [{ turnId, createdAt, userInputText, finalChatResponse, todoPlan,
 *             scopePlan, thumbsUp, thumbsDown, feedbackComment }],
 *   outputsByTurn: { [turnId]: [{ turnOutputId, outputType, content, createdAt }] },
 *   stateSnapshot: { dealRegistry, documentRegistry, facts },
 *   latestWorkspacePayload: { turnId, createdAt, results, documents, facts,
 *                             projectId, source } | null
 * }
 *
 * Phase 3: UI rehydration uses latestWorkspacePayload (the canonical snapshot)
 * instead of manually scanning outputsByTurn for results_payload entries.
 */
export const rehydrateConversation =
  (projectId: string) =>
  async (dispatch: any, getState: any): Promise<void> => {
    console.log("🔄 [REHYDRATE] rehydrateConversation called for projectId:", projectId);

    // Temporary projects have no conversation history yet
    if (projectId.startsWith(TEMP_PROJECT_PREFIX)) {
      console.log("🔄 [REHYDRATE] Temporary project detected - skipping API call");
      finishWithNoHistory(dispatch);
      return;
    }

    // Cancels any prior load
    const abortController = createConversationLoadAbortController();

    dispatch(setChatHistoryLoading(true));
    dispatch(setChatHistoryError(null));

    try {
      const history = await fetchHistory(projectId, abortController.signal);

      // The user may have switched to another project/chat while we waited
      if (abortController.signal.aborted) {
        console.log("🛑 [REHYDRATE] Request was cancelled - aborting rehydration");
        return;
      }
      console.log("🔄 [REHYDRATE] Received history:", history);

      if (!history.turns || history.turns.length === 0) {
        console.log("🔄 [REHYDRATE] No turns to rehydrate - keeping welcome message");
        finishWithNoHistory(dispatch);
        return;
      }

      applyRehydratedHistory(history, dispatch, getState);

      console.log("✅ [REHYDRATE] Conversation rehydration complete");
      dispatch(setChatHistoryLoading(false));
    } catch (error) {
      reportRehydrationFailure(error, dispatch);
    }
  };
